import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CustomModalHeader from '../../../Common/customComponents/CustomModalHeader.tsx';
import { BasicModalV2, TextButton } from '../../../../components';

import DeviceCctv from '/assets/icon/global/device-cctv.svg';
import DeviceGasDetector from '/assets/icon/global/device-gas-detector.svg';
import DeviceHook from '/assets/icon/global/device-hook.svg';
import CustomText from '../../../Common/customComponents/CustomText.tsx';
import CustomTable from '../../../Common/customComponents/CustomTable.tsx';
import { useAsync } from '../../../../hooks';
import FormTabXBlack from '/assets/icon/global/form-tab-x-black.svg';
import { ptwApi } from '../../../../api/ptw.api.ts';
import BatteryItem from '../component/etc/BatteryItem.tsx';
import { commonMsg } from 'utils/globalUtil.tsx';
import { useSetRecoilState } from 'recoil';
import { LoadingAtom } from '../../../../modules';

interface IProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  title?: string;
  onActionCallback: any;
  gubun?: string;
  targetPtwDSeq: number;
  iotDeviceList?: Record<string, any[]>;
}

const DeviceRegisterModal = (props: IProps) => {
  const setLoading = useSetRecoilState(LoadingAtom);
  /*일반*/
  const [searchData, setSearchData] = useState<any>({});
  const [gubun, setGubun] = useState<any>(null);
  const { callAsync, showAlarm, closeAlarm } = useAsync();

  /*테이블관련*/
  const [gridData, setGridData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [tableSort, setTableSort] = useState<any>({});
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [checkedMap, setCheckedMap] = useState<Record<string, any[]>>({
    cctv: [],
    gasDetector: [],
    smartHook: []
  });
  const [isModalOpen, setIsModalOpen] = useState<any>(false);

  const checkedList = checkedMap[gubun] ?? [];
  const checkedMapRef = useRef(checkedMap);
  checkedMapRef.current = checkedMap;

  const setCheckedList = useCallback(
    (updater: any) => {
      const prev = checkedMapRef.current;
      const newList = typeof updater === 'function' ? updater(prev[gubun] ?? []) : updater;
      setCheckedMap({ ...prev, [gubun]: newList });
    },
    [gubun]
  );

  useEffect(() => {
    //setIsModalOpen(props.isModalOpen)
    if (props.isModalOpen) {
      setGubun(props.gubun);
      setCurrentPage(1);
      setCheckedMap({
        cctv: props.iotDeviceList?.cctv ?? [],
        gasDetector: props.iotDeviceList?.gasDetector ?? [],
        smartHook: props.iotDeviceList?.smartHook ?? []
      });
    }
  }, [props.isModalOpen]);

  useEffect(() => {
    //console.warn(currentPage, searchData, tableSort.sortColumn, tableSort.sortValue, pageSize)
    if (props.isModalOpen) {
      search(1);
    }
  }, [gubun]);

  useEffect(() => {
    //console.warn(currentPage, searchData, tableSort.sortColumn, tableSort.sortValue, pageSize)
    if (props.isModalOpen) {
      search(currentPage);
    }
  }, [currentPage, searchData, tableSort.sortColumn, tableSort.sortValue, pageSize]);

  const handleChange = (e: any) => {
    setSearchData((oData: any) => {
      return { ...oData, [e.target.name]: e.target.value };
    });
  };

  const search = (pageNum?: number) => {
    setLoading(true);
    const param = {
      ...searchData,
      gubun: gubun,
      targetPtwDSeq: props.targetPtwDSeq,
      page: pageNum,
      size: pageSize,
      ...(tableSort?.sortColumn && tableSort?.sortValue
        ? { sort: `${tableSort.sortColumn},${tableSort.sortValue}` }
        : {})
    };

    const currentChecked = checkedMap[gubun] ?? [];
    ptwApi
      .getDeviseList(param)
      .then(function (r) {
        const resultList = r.resultList.map((row: any, index: number) => ({
          ...row,
          no: index + 1 + (currentPage - 1) * pageSize,
          isCheck: currentChecked.some((c: any) => c.deviceId === row.deviceId)
        }));

        setGridData(resultList);
        setTotalCount(r.resultList[0]?.totalCount);
      })
      .finally(function () {
        setLoading(false);
      });
  };
  const columns = useMemo(() => {
    const baseColumns = [
      { column: 'no', label: 'No', align: 'center', sort: false, size: 80 },
      {
        column: 'deviceName',
        label: 'Name',
        align: 'center',
        sort: false,
        size: 150,
        type: 'custom',
        custom: (row: any) =>
          row.serialNumber ? `${row.deviceName}(${row.serialNumber})` : row.deviceName
      },
      {
        column: 'battery1',
        label: 'Battery1',
        align: 'center',
        sort: false,
        size: 80,
        type: 'custom',
        custom: (row: any) => (
          <div className="flex-row flex-row-just-center text-align-center gap-3">
            <BatteryItem percent={row.battery1} />
            <span>{row.battery1}%</span>
          </div>
        )
      },
      {
        column: 'battery2',
        label: 'Battery2',
        align: 'center',
        sort: false,
        size: 80,
        type: 'custom',
        custom: (row: any) => (
          <div className="flex-row flex-row-just-center text-align-center gap-3">
            <BatteryItem percent={row.battery2} />
            <span>{row.battery2}%</span>
          </div>
        )
      }
    ];

    if (gubun === 'cctv') {
      return baseColumns.filter((col) => col.column !== 'battery1' && col.column !== 'battery2');
    } else if (gubun === 'gasDetector') {
      return baseColumns.filter((col) => col.column !== 'battery2');
    }

    return baseColumns;
  }, [gubun]);

  const deleteItem = (targetItem: any) => {
    setCheckedList((prevList: any) =>
      prevList.filter((oItem: any) => oItem.deviceId !== targetItem.deviceId)
    );
  };

  const modalClose = (p0: boolean) => {
    props.setIsModalOpen(false);
  };

  const save = () => {
    const hasAny = Object.values(checkedMap).some((list) => list.length > 0);
    if (!hasAny) {
      commonMsg.alert(showAlarm, closeAlarm, 'Warning', 'No items selected.', null);
      return;
    }
    props.onActionCallback(checkedMap);
    props.setIsModalOpen(false);
  };

  return (
    <BasicModalV2 open={props.isModalOpen} onClose={() => props.setIsModalOpen(false)}>
      <CustomModalHeader title={props.title || 'Device Register'} modalClose={modalClose} />
      <div
        className={'custom-modal-body device-register-modal'}
        style={{ width: '700px', height: '800px' }}
      >
        <div className={'contents-box'}>
          <div className={'select-area'}>
            <span className={'select-message'}>
              Please enter the information you would like to add.
            </span>
            <div className={'select-list'}>
              <div
                className={`select-item ${gubun == 'cctv' ? 'active' : ''}`}
                onClick={() => setGubun('cctv')}
              >
                <div>
                  <img src={DeviceCctv} alt={'DeviceCctv'} />
                </div>
                <div className={'select-item-text'}>
                  <span>CCTV</span>
                </div>
              </div>
              <div
                className={`select-item ${gubun == 'gasDetector' ? 'active' : ''}`}
                onClick={() => setGubun('gasDetector')}
              >
                <div>
                  <img src={DeviceGasDetector} alt={'DeviceGasDetector'} />
                </div>
                <div className={'select-item-text'}>
                  <span>Gas</span>
                  <span>Detector</span>
                </div>
              </div>
              <div
                className={`select-item ${gubun == 'smartHook' ? 'active' : ''}`}
                onClick={() => setGubun('smartHook')}
              >
                <div>
                  <img src={DeviceHook} alt={'DeviceHook'} />
                </div>
                <div className={'select-item-text'}>
                  <span>Hook</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={'contents-box'}>
          <div className={'search-box'}>
            <div className={'search-item-list'}>
              <div className={'search-item'}>
                <CustomText
                  onChange={handleChange}
                  name={'searchText'}
                  value={searchData?.searchText}
                  isBtnClose={false}
                  isBtnSearch={true}
                  style={{ width: '300px' }}
                  placeholder={'Enter search query.'}
                />
              </div>
            </div>
            <div className={'page-info'}>
              <span>[Total : {totalCount}]</span>
            </div>
          </div>
          <div className={'grid-box mb-20'}>
            <CustomTable
              columns={columns}
              data={gridData}
              setData={setGridData}
              isPaging
              isCheck={true}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalCount={totalCount}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setTableSort={setTableSort}
              className={'min-w-100'}
              setSelectedRow={setSelectedRow}
              checkedList={checkedList}
              setCheckedList={setCheckedList}
              bodyHeight={200}
              dbClick={(row) => {}}
            />
          </div>
          <div className={'flex-col gap-3 mb-20'}>
            <div className={'page-info'}>
              <span>[Total : {totalCount}]</span>
            </div>
            <div className={'selected-item-list'}>
              {checkedList.map((item: any, index: number) => (
                <div className={'selected-item'} key={index}>
                  <span>{item.deviceName}</span>
                  <a
                    onClick={() => {
                      deleteItem(item);
                    }}
                  >
                    <img
                      src={FormTabXBlack}
                      alt={'close'}
                      style={{ width: '15px', height: '15px' }}
                    />
                  </a>
                </div>
              ))}
            </div>
          </div>
          <div className={'button-box'}>
            <TextButton
              className={'cancel-style'}
              variant="outlined"
              style={{ height: '40px' }}
              onClick={() => {
                modalClose(false);
              }}
            >
              Close
            </TextButton>

            <TextButton
              variant="contained"
              style={{ height: '40px' }}
              onClick={() => {
                save();
              }}
            >
              SAVE
            </TextButton>
          </div>
        </div>
      </div>
    </BasicModalV2>
  );
};

export default DeviceRegisterModal;
